import { useMemo } from 'react'
import { ImmutableVector3, toImmutable } from '@modugen/ui-toolkit'
import { interpolate } from '@utils'
import { getLength } from '../utils'

const relativeArrowLength = 0.9

interface Arguments {
  slab: ShapeObject
  offset?: number
}

const useSlabOrientation = ({ slab, offset = 0.001 }: Arguments) => {
  return useMemo(() => {
    const { points } = slab.shape
    // arrow direction is orthogonal to direction of first two points
    const p0 = points[0]
    const p1 = points[1]
    const p2 = points[2]
    const p3 = points[3]
    const pOther = points[points.length - 1]

    const vx = p1.sub(p0)
    const vy = pOther.sub(p0)

    const vz = new ImmutableVector3().crossVectors(vx, vy)
    const spanDirection = new ImmutableVector3().crossVectors(vz, vx).normalize()

    const pEdge0Center = interpolate(p0, p1, 0.5)
    const pEdge2Center = interpolate(p2, p3, 0.5)
    const origin = interpolate(pEdge0Center, pEdge2Center, (1 - relativeArrowLength) / 2)
    const lengthAtCenter = getLength(pEdge0Center, pEdge2Center)

    const length = lengthAtCenter * relativeArrowLength

    return {
      direction: spanDirection,
      length: length,
      center: toImmutable(origin.v.setZ(origin.z + offset)),
      normal: vz.normalize(),
    }
  }, [slab.shape, offset])
}

export default useSlabOrientation
